export type DateRangeMode = "today" | "custom";

export type DateRangePreset =
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "thisMonth";

export interface PartnerDashboardDateRange {
  mode: DateRangeMode;
  start: Date;
  end: Date;
}

export function todayRange(): PartnerDashboardDateRange {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { mode: "today", start, end };
}

/** Local-day boundaries for a preset (shared by the date control + report defaults). */
export function presetRange(preset: DateRangePreset): { start: Date; end: Date } {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  if (preset === "yesterday") {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  } else if (preset === "last7") {
    start.setDate(start.getDate() - 6);
  } else if (preset === "last30") {
    start.setDate(start.getDate() - 29);
  } else if (preset === "thisMonth") {
    start.setDate(1);
  }
  return { start, end };
}

/** Report pages default to a wider window than the live Today view. */
export function rangeFromPreset(
  preset: Exclude<DateRangePreset, "today">
): PartnerDashboardDateRange {
  const { start, end } = presetRange(preset);
  return { mode: "custom", start, end };
}

export function formatDashboardRangeLabel(
  range: PartnerDashboardDateRange,
  todayLabel = "Today"
): string {
  if (range.mode === "today") return todayLabel;
  const fmt = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  });
  const start = fmt.format(range.start);
  const end = fmt.format(range.end);
  if (start === end) return start;
  return `${start} – ${end}`;
}

export function parseDateInput(value: string): Date | null {
  if (!value) return null;
  const d = new Date(`${value}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function isInDateRange(
  iso: string | null,
  range: PartnerDashboardDateRange
): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return t >= range.start.getTime() && t <= range.end.getTime();
}
